import React, { useState } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars, faXmark } from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import './App.css';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './toastTheme.css';
import { SocketProvider } from './components/SharedView/contexts/SocketContext';
import { NotificationProvider } from './components/SharedView/contexts/NotificationContext';
import GlobalNotificationListener from './components/SharedView/GlobalNotificationListener';
import Sidebar from './components/SharedView/Sidebar';
import { isPaymentReturnActive } from './components/ClientView/usePaymentReturn';

// CLIENTS
import Clients from './components/StaffView/Clients/Clients';
import ClientDetails from './components/StaffView/Clients/ClientDetails';
import AddClient from './components/StaffView/Clients/AddClient';
import EditClient from './components/StaffView/Clients/EditClient';

// PROPERTIES
import Properties from './components/StaffView/Properties/Properties';
import PropertyDetails from './components/StaffView/Properties/PropertyDetails';
import AddProperty from './components/StaffView/Properties/AddProperty/AddProperty';
import EditProperty from './components/StaffView/Properties/EditProperty/EditProperty';

// BOOKINGS
import Bookings from './components/StaffView/Bookings/Bookings';
import BookingDetails from './components/StaffView/Bookings/BookingDetails';
import Leads from './components/StaffView/Leads/Leads';
import AddBooking from './components/StaffView/Bookings/AddBooking';
import EditBooking from './components/StaffView/Bookings/EditBooking';
import DailyBookings from './components/StaffView/Bookings/DailyBookings';
import SummaryReport from './components/StaffView/Bookings/SummaryReport';
import Scheduling from './components/StaffView/Bookings/Scheduling/Scheduling';


// TEAM
import Team from './components/StaffView/Team/Team';

// LAUNDRY
import Laundry from './components/StaffView/Laundry/Laundry';
import AddLaundry from './components/StaffView/Laundry/AddLaundry';
import ConfirmLaundry from './components/StaffView/Laundry/ConfirmLaundry';
import LaundryDetails from './components/StaffView/Laundry/LaundryDetails/LaundryDetails';
import EditLaundry from './components/StaffView/Laundry/EditLaundry';
import PackingLaundry from './components/StaffView/Laundry/PackingLaundry';
import LinenHire from './components/StaffView/Laundry/LinenHire'
import LaundryStats from './components/StaffView/Laundry/LaundryStats'
import ReportLHStain from './components/StaffView/Laundry/ReportLHStain';

// WAREHOUSE
import Warehouse from './components/StaffView/Warehouse/Warehouse';
import Stock from './components/StaffView/Warehouse/Stock/Stock';

// Drivers
import Drivers from './components/StaffView/Drivers/Drivers';

// Items
import Items from './components/StaffView/Items/Items'
import Salaries from './components/StaffView/Salaries/Salaries'

// Audit Logs
import AuditLogs from './components/StaffView/AuditLogs';
// Client-requests history (who approved what)
import RequestsHistory from './components/StaffView/ClientRequests/RequestsHistory';
import InvoicesManagement from './components/StaffView/InvoicesManagement';

// Statistics
import Statistics from './components/StaffView/Statistics/Statistics';
import WarningLetters from './components/StaffView/Tools/WarningLetters';
import CoverageChecker from './components/StaffView/Tools/CoverageChecker';


/* ################ SHARED VIEW VIEW ################ */
// Settings
import Settings from './components/StaffView/Settings';
import Login from './components/SharedView/Settings/Login';
import RegisterPage from './components/SharedView/Settings/RegisterPage';
import ResetPassword from './components/SharedView/Settings/ResetPassword';
import ForgotPassword from './components/SharedView/Settings/ForgotPassword';
import Logout from './components/SharedView/Settings/Logout'

import ProtectedRoute from './components/SharedView/ProtectedRoute';


// HOME
import Home from './components/StaffView/Home';


/* ################ CLIENT VIEW ################ */
import ClientHome from './components/ClientView/ClientHome';
import PaymentReturnPage from './components/ClientView/PaymentReturnPage';
import PublicPaymentComplete from './components/ClientView/PublicPaymentComplete';
import PublicInvoicePay from './components/ClientView/PublicInvoicePay';
import PublicAccountPay from './components/ClientView/AccountPay/PublicAccountPay';
import ClientSettings from './components/ClientView/ClientSettings';
import ClientProfile from './components/ClientView/ClientProfile/ClientProfile';
import ClientTasks from './components/ClientView/ClientTasks/ClientTasks';

// Bookings
import ClientBookings from './components/ClientView/ClientBookings/ClientBookings';
import ClientBookingDetails from './components/ClientView/ClientBookings/DetailsComponents/ClientBookingDetails';
import BookingRequestDetails from './components/ClientView/ClientRequests/BookingRequestDetails';
// B1 (2026-07-19): booking request + edit are PAGES, not modals
import RequestBookingPage from './components/ClientView/ClientRequests/RequestBookingPage';
import RequestBookingEditPage from './components/ClientView/ClientRequests/RequestBookingEditPage';

// Properties
import ClientProperties from './components/ClientView/ClientProperties/ClientProperties';
import ClientPropertyDetails from './components/ClientView/ClientProperties/ClientPropertyDetails';

// Payments
import ClientInvoices from './components/ClientView/ClientInvoices/ClientInvoices';
import ClientInvoicePay from './components/ClientView/ClientInvoices/ClientInvoicePay';
import ClientPayments from './components/ClientView/ClientInvoices/ClientPayments';

// Reports
import ClientSummaryReport from './components/ClientView/ClientReports/ClientSummaryReport';

// Wallet
import ClientWallet from './components/ClientView/ClientWallet/ClientWallet';
import PendingDeposits from './components/StaffView/Wallet/PendingDeposits';
// Old bookmarked/notified /wallet/clients/:id links land on the client page,
// where the wallet section now lives.
function WalletRedirect() {
  const { clientId } = useParams();
  return <Navigate to={`/clients/${clientId}`} replace />;
}

// Check user role before rendering anything
const getUserRole = () => {
  return localStorage.getItem('userRole');
};

// ✅ Set this once globally
axios.defaults.withCredentials = true;

// Interceptor to customize requests if needed
axios.interceptors.request.use(
  (config) => {
    // You could add custom headers here if needed in future
    return config;
  },
  (error) => Promise.reject(error)
);

// AUTO-RELOAD ON A NEW DEPLOY (owner, 2026-09-09: "after every update I have
// to hard-refresh or I get errors everywhere"). Every API response carries
// X-Portal-Rev — the revision the backend was deployed from — and this bundle
// was built with REACT_APP_PORTAL_REV. When they differ, this tab is an old
// build talking to a new backend, which is exactly where those errors come
// from; it says so and reloads. index.html is served no-store, so the reload
// picks up the new build. A tab that reloaded and STILL mismatches stops
// after one go and asks for a hard refresh rather than looping.
const BUILT_REV = process.env.REACT_APP_PORTAL_REV || '';
let revReloadArmed = false;
function noteServerRev(headers) {
  const serverRev = headers ? headers['x-portal-rev'] : null;
  if (!BUILT_REV || !serverRev || serverRev === BUILT_REV || revReloadArmed) return;
  revReloadArmed = true;
  let already = '';
  try { already = sessionStorage.getItem('portalRevReloaded') || ''; } catch (_) { /* storage blocked */ }
  if (already === serverRev) {
    toast.warn('The portal was updated but this tab is still on the old version — please hard-refresh (Cmd/Ctrl + Shift + R).', { autoClose: false });
    return;
  }
  try { sessionStorage.setItem('portalRevReloaded', serverRev); } catch (_) { /* storage blocked */ }
  toast.info('The portal was just updated — reloading…', { autoClose: 1500 });
  setTimeout(() => window.location.reload(), 1600);
}

axios.interceptors.response.use(
  (response) => { noteServerRev(response.headers); return response; },
  async (error) => {
    noteServerRev(error.response?.headers);
    const isLoginRoute = error.config?.url?.includes('/api/login');

    // While a hosted-paypage return is being verified (?payment=return&ref=…),
    // a 401 must NOT log the user out and dump the raw login screen mid-flow —
    // the PaymentResultOverlay handles it with a calm "log in to see it" card
    // that preserves the return URL. (Clearing localStorage here would also
    // let ProtectedRoute bounce the page and destroy the overlay.)
    if (error.response?.status === 401 && !isLoginRoute && !isPaymentReturnActive()) {
      try {
        await axios.post('/api/login/logout', {}, { withCredentials: true });
      } catch (logoutError) {
        console.error('Logout cleanup failed:', logoutError);
      }

      localStorage.clear();
      localStorage.removeItem('userRole');
      localStorage.removeItem('firstName');
      localStorage.removeItem('userId');
      localStorage.removeItem('clientId'); // if applicable
      sessionStorage.clear();
      window.location.href = '/login';
    }

    return Promise.reject(error);
  }
);


function App() {

  return (
    <NotificationProvider>
      <SocketProvider>
        <Router
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}>
          <GlobalNotificationListener />
          <AppLayout />
        </Router>
      </SocketProvider>
    </NotificationProvider>
  );
}


function AppLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Desktop/Tablet collapse (persist)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    return localStorage.getItem('sidebarCollapsed') === '1';
  });

  const location = useLocation();
  const userRole = getUserRole();

  const toggleSidebar = () => setSidebarOpen((prev) => !prev);

  const toggleSidebarCollapsed = () => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem('sidebarCollapsed', next ? '1' : '0');
      return next;
    });
  };


  // 🔒 Routes where we DON'T want sidebar / dashboard layout
  const hideSidebar =
    location.pathname === '/login' ||
    location.pathname === '/register' ||
    location.pathname === '/forgot-password' ||
    location.pathname === '/logout' ||
    location.pathname.startsWith('/reset-password') ||
    // Payment return legs render as standalone branded pages — no dashboard
    // chrome behind the result overlay (and /payment-complete is public).
    location.pathname === '/payment-complete' ||
    // Shareable guest pay page — ALWAYS chrome-free, even when a staff session
    // exists in the same browser (2026-08-09: it looked like a logged-in
    // page in review shots).
    location.pathname.startsWith('/pay/') ||
    location.pathname.startsWith('/client/payment-return');

  const routes = (
    <>
      <ToastContainer
        position="top-right"
        autoClose={3200}
        limit={3}
        newestOnTop
        closeOnClick
        pauseOnFocusLoss={false}
        draggable
        hideProgressBar
        theme="light"
      />
      {/* Business clients must have their trade license (and VAT certificate
          when VAT-registered) on file before using the portal. Client
          sessions only — staff and admin edits are never gated — and never on
          auth/payment-return pages, where blocking would strand a payment. */}
      {/* RequiredDocsGate no longer auto-mounts (2026-08-07: no blocking
          modal on account pages — documents are enforced INSIDE the account-
          edit flow and the registration checklist instead). */}
      <Routes>
        {/* Public auth routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password/:token" element={<ResetPassword />} />
        <Route path="/logout" element={<Logout />} />

        {/* PUBLIC payment completion page — mobile in-app browser checkouts
            land here with no web session; shows outcome + amount only. */}
        <Route path="/payment-complete" element={<PublicPaymentComplete />} />
        {/* Shareable HHC pay page — token IS the authorization, no login. */}
        <Route path="/pay/:token" element={<PublicInvoicePay />} />
        {/* Permanent per-client pay link — PLAN_payment_links.md. Public by
            design; the token names a client and authorises seeing what they owe
            and paying it. Declared BEFORE /pay/:token would otherwise swallow
            "account" as a token. */}
        <Route path="/pay/account/:token" element={<PublicAccountPay />} />

        {/* Hosted-paypage return legs (query-free routes; the gateway appends
            ?ref=). Deliberately NOT behind ProtectedRoute: with no session the
            verify call 401s and the overlay shows a calm login CTA that
            round-trips back here — instead of the raw login screen. */}
        <Route path="/client/payment-return/:surface" element={<PaymentReturnPage />} />

        {/* Protected Routes */}
        <Route
          path="/"
          element={
            userRole === 'client' ? (
              <Navigate to="/client/home" replace />
            ) : (
              <ProtectedRoute allowedRoles={['admin', 'manager', 'warehouse', 'driver', 'supervisor']}>
                <Home />
              </ProtectedRoute>
            )
          }
        />

        {/* Warehouse */}
        <Route
          path="/warehouse"
          element={
            <ProtectedRoute allowedRoles={['admin', 'manager', 'warehouse', 'supervisor']}>
              <Warehouse />
            </ProtectedRoute>
          }
        />

        {/* Warehouse stock: request → ordered → delivered (routes/stock.js) */}
        <Route
          path="/warehouse/stock"
          element={
            <ProtectedRoute allowedRoles={['admin', 'manager', 'warehouse', 'supervisor']}>
              <Stock />
            </ProtectedRoute>
          }
        />

        {/* Clients */}
        <Route
          path="/clients"
          element={
            <ProtectedRoute allowedRoles={['admin', 'manager', 'supervisor']}>
              <Clients />
            </ProtectedRoute>
          }
        />
        <Route
          path="/clients/:clientId"
          element={
            <ProtectedRoute allowedRoles={['admin', 'manager', 'supervisor']}>
              <ClientDetails />
            </ProtectedRoute>
          }
        />
        <Route
          path="/clients/new"
          element={
            <ProtectedRoute allowedRoles={['admin', 'manager', 'supervisor']}>
              <AddClient />
            </ProtectedRoute>
          }
        />
        <Route
          path="/clients/edit/:clientId"
          element={
            <ProtectedRoute allowedRoles={['admin', 'manager', 'supervisor']}>
              <EditClient />
            </ProtectedRoute>
          }
        />

        {/* Tools (owner, 2026-09-21) — first tool: payment warning letters */}
        <Route
          path="/tools/warning-letters"
          element={
            <ProtectedRoute allowedRoles={['admin', 'manager', 'supervisor']}>
              <WarningLetters />
            </ProtectedRoute>
          }
        />

        <Route
          path="/tools/coverage"
          element={
            <ProtectedRoute allowedRoles={['admin', 'manager', 'supervisor']}>
              <CoverageChecker />
            </ProtectedRoute>
          }
        />

        {/* Properties */}
        <Route
          path="/properties"
          element={
            <ProtectedRoute allowedRoles={['admin', 'manager', 'supervisor']}>
              <Properties />
            </ProtectedRoute>
          }
        />
        <Route
          path="/properties/new"
          element={
            <ProtectedRoute allowedRoles={['admin', 'manager', 'supervisor']}>
              <AddProperty />
            </ProtectedRoute>
          }
        />
        <Route
          path="/properties/:propertyId"
          element={
            <ProtectedRoute allowedRoles={['admin', 'manager', 'supervisor']}>
              <PropertyDetails />
            </ProtectedRoute>
          }
        />
        <Route
          path="/properties/edit/:propertyId"
          element={
            <ProtectedRoute allowedRoles={['admin', 'manager', 'supervisor']}>
              <EditProperty />
            </ProtectedRoute>
          }
        />

        {/* Bookings */}
        <Route
          path="/bookings"
          element={
            <ProtectedRoute allowedRoles={['admin', 'manager', 'supervisor']}>
              <Bookings />
            </ProtectedRoute>
          }
        />
        <Route
          path="/bookings/:bookingId"
          element={
            <ProtectedRoute allowedRoles={['admin', 'manager', 'supervisor']}>
              <BookingDetails />
            </ProtectedRoute>
          }
        />
        {/* New-inquiry pipeline (PLAN_leads.md). Open to the whole office —
            these are the people who answer the messages. */}
        <Route
          path="/leads"
          element={
            <ProtectedRoute allowedRoles={['admin', 'manager', 'supervisor']}>
              <Leads />
            </ProtectedRoute>
          }
        />
        <Route
          path="/bookings/new"
          element={
            <ProtectedRoute allowedRoles={['admin', 'manager', 'supervisor']}>
              <AddBooking />
            </ProtectedRoute>
          }
        />
        <Route
          path="/bookings/edit/:bookingId"
          element={
            <ProtectedRoute allowedRoles={['admin', 'manager', 'supervisor']}>
              <EditBooking />
            </ProtectedRoute>
          }
        />
        <Route
          path="/bookings/report/:date"
          element={
            <ProtectedRoute allowedRoles={['admin', 'manager', 'supervisor']}>
              <DailyBookings />
            </ProtectedRoute>
          }
        />
        <Route
          path="/bookings/summary-report"
          element={
            <ProtectedRoute allowedRoles={['admin', 'manager', 'supervisor']}>
              <SummaryReport />
            </ProtectedRoute>
          }
        />
        <Route
          path="/bookings/scheduling/:date"
          element={
            <ProtectedRoute allowedRoles={['admin', 'manager', 'supervisor']}>
              <Scheduling />
            </ProtectedRoute>
          }
        />

        {/* Team */}
        <Route
          path="/team"
          element={
            <ProtectedRoute allowedRoles={['admin', 'manager', 'supervisor']}>
              <Team />
            </ProtectedRoute>
          }
        />
        <Route
          path='/audit-logs'
          element={
            <ProtectedRoute allowedRoles={['manager', 'supervisor']}>
              <AuditLogs />
            </ProtectedRoute>
          }
        />
        <Route
          path='/requests'
          element={
            <ProtectedRoute allowedRoles={['admin', 'manager', 'supervisor']}>
              <RequestsHistory />
            </ProtectedRoute>
          }
        />

        <Route
          path='/invoices'
          element={
            <ProtectedRoute allowedRoles={['manager']}>
              <InvoicesManagement />
            </ProtectedRoute>
          }
        />

        <Route
          path='/statistics'
          element={
            <ProtectedRoute allowedRoles={['admin', 'manager', 'supervisor']}>
              <Statistics />
            </ProtectedRoute>
          }
        />

        {/* Payments (owner, 2026-09-02): the wallet queue page lives at
            /payments now, and the standalone wallet pages folded into Clients.
            The /wallet/* paths REDIRECT rather than die — years of WhatsApp
            and email notifications carry those links. */}
        <Route
          path="/payments"
          element={
            <ProtectedRoute allowedRoles={['admin', 'manager', 'supervisor']}>
              <PendingDeposits />
            </ProtectedRoute>
          }
        />
        <Route path="/wallet" element={<Navigate to="/clients" replace />} />
        <Route path="/wallet/pending" element={<Navigate to="/payments" replace />} />
        <Route path="/wallet/clients/:clientId" element={<WalletRedirect />} />

        {/* Laundry */}
        <Route
          path="/laundry"
          element={
            <ProtectedRoute allowedRoles={['admin', 'manager', 'warehouse', 'supervisor']}>
              <Laundry />
            </ProtectedRoute>
          }
        />
        <Route
          path="/laundry/new"
          element={
            <ProtectedRoute allowedRoles={['admin', 'manager', 'warehouse', 'supervisor']}>
              <AddLaundry />
            </ProtectedRoute>
          }
        />
        <Route
          path="/laundry/confirm/:id"
          element={
            <ProtectedRoute allowedRoles={['manager', 'warehouse', 'supervisor']}>
              <ConfirmLaundry />
            </ProtectedRoute>
          }
        />
        <Route
          path="/laundry/packing/:id"
          element={
            <ProtectedRoute allowedRoles={['manager', 'warehouse', 'supervisor']}>
              <PackingLaundry />
            </ProtectedRoute>
          }
        />
        <Route
          path="/laundry/:bookingId"
          element={
            <ProtectedRoute allowedRoles={['admin', 'manager', 'warehouse', 'supervisor']}>
              <LaundryDetails />
            </ProtectedRoute>
          }
        />
        <Route
          path="/laundry/edit/:id"
          element={
            <ProtectedRoute allowedRoles={['admin', 'manager', 'supervisor']}>
              <EditLaundry />
            </ProtectedRoute>
          }
        />
        <Route
          path="/linen-hire"
          element={
            <ProtectedRoute allowedRoles={['admin', 'manager', 'supervisor']}>
              <LinenHire />
            </ProtectedRoute>
          }
        />
        <Route
          path="/laundry/stats"
          element={
            <ProtectedRoute allowedRoles={['manager']}>
              <LaundryStats />
            </ProtectedRoute>
          }
        />
        <Route
          path="/laundry/report-lh-stain"
          element={
            <ProtectedRoute allowedRoles={['admin', 'manager', 'warehouse', 'supervisor']}>
              <ReportLHStain />
            </ProtectedRoute>
          }
        />

        {/* Drivers */}
        <Route
          path="/drivers"
          element={
            <ProtectedRoute allowedRoles={['driver', 'manager', 'admin', 'supervisor']}>
              <Drivers />
            </ProtectedRoute>
          }
        />

        {/* Items */}
        <Route
          path="/items"
          element={
            <ProtectedRoute allowedRoles={['manager', 'admin', 'supervisor']}>
              <Items />
            </ProtectedRoute>
          }
        />

        {/* Salaries / payroll page. Managers get the full page with Salaries +
            Attendance tabs. Admins & supervisors reach the SAME page from the
            Team button (deep-linked to ?tab=attendance) and only see the
            Attendance tab (no pay data); the sidebar links it for managers only. */}
        <Route
          path="/salaries"
          element={
            <ProtectedRoute allowedRoles={['manager', 'admin', 'supervisor']}>
              <Salaries />
            </ProtectedRoute>
          }
        />

        <Route
          path="/settings"
          element={
            <ProtectedRoute
              allowedRoles={['admin', 'manager', 'warehouse', 'driver', 'cleaner', 'supervisor']}
            >
              <Settings />
            </ProtectedRoute>
          }
        />

        {/* ######## CLIENT VIEW ######## */}
        <Route
          path="/client/home"
          element={
            <ProtectedRoute allowedRoles={['client']}>
              <ClientHome />
            </ProtectedRoute>
          }
        />
        <Route
          path="/client/bookings"
          element={
            <ProtectedRoute allowedRoles={['client']}>
              <ClientBookings />
            </ProtectedRoute>
          }
        />
        {/* B1: the booking request form as a PAGE (deep-linkable; the
            static "request" segment outranks :bookingId below). */}
        <Route
          path="/client/bookings/request"
          element={
            <ProtectedRoute allowedRoles={['client']}>
              <RequestBookingPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/client/bookings/:bookingId/edit-request"
          element={
            <ProtectedRoute allowedRoles={['client']}>
              <RequestBookingEditPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/client/bookings/requests/:requestId"
          element={
            <ProtectedRoute allowedRoles={['client']}>
              <BookingRequestDetails />
            </ProtectedRoute>
          }
        />
        <Route
          path="/client/bookings/:bookingId"
          element={
            <ProtectedRoute allowedRoles={['client']}>
              <ClientBookingDetails />
            </ProtectedRoute>
          }
        />
        <Route
          path="/client/properties"
          element={
            <ProtectedRoute allowedRoles={['client']}>
              <ClientProperties />
            </ProtectedRoute>
          }
        />
        <Route
          path="/client/properties/:propertyId"
          element={
            <ProtectedRoute allowedRoles={['client']}>
              <ClientPropertyDetails />
            </ProtectedRoute>
          }
        />
        <Route
          path="/client/invoices"
          element={
            <ProtectedRoute allowedRoles={['client']}>
              <ClientInvoices />
            </ProtectedRoute>
          }
        />
        {/* Paying the pending invoices is its OWN page (2026-08-14) — the
            card form, saved cards, Apple Pay and the wallet split never fitted
            in a modal. Declared BEFORE /client/invoices/:anything would be, and
            it is a plain sibling route, so a refresh mid-payment lands here. */}
        <Route
          path="/client/invoices/pay"
          element={
            <ProtectedRoute allowedRoles={['client']}>
              <ClientInvoicePay />
            </ProtectedRoute>
          }
        />
        <Route
          path="/client/payments"
          element={
            <ProtectedRoute allowedRoles={['client']}>
              <ClientPayments />
            </ProtectedRoute>
          }
        />

        <Route
          path="/client/wallet"
          element={
            <ProtectedRoute allowedRoles={['client']}>
              <ClientWallet />
            </ProtectedRoute>
          }
        />

        {/* Price adjustments RETIRED (2026-07-21) — /client/adjustments
            and its page are gone. Money a booking owes now shows as the
            booking's pending amount + price-change history. */}

        <Route
          path="/client/reports"
          element={
            <ProtectedRoute allowedRoles={['client']}>
              <ClientSummaryReport />
            </ProtectedRoute>
          }
        />

        <Route
          path="/client/settings"
          element={
            <ProtectedRoute allowedRoles={['client']}>
              <ClientSettings />
            </ProtectedRoute>
          }
        />

        <Route
          path="/client/profile"
          element={
            <ProtectedRoute allowedRoles={['client']}>
              <ClientProfile />
            </ProtectedRoute>
          }
        />

        <Route
          path="/client/tasks"
          element={
            <ProtectedRoute allowedRoles={['client']}>
              <ClientTasks />
            </ProtectedRoute>
          }
        />

      </Routes>
    </>
  );

  // 🔧 If we're on a login/auth page → no sidebar + no dashboard layout
  if (hideSidebar) {
    return routes;
  }

  // 🔧 Normal dashboard layout with sidebar
  return (
    <div className="application">
      <button
        type="button"
        className={`mobile-menu-button ${sidebarOpen ? 'open' : ''}`}
        onClick={toggleSidebar}
        aria-label={sidebarOpen ? 'Close menu' : 'Open menu'}
      >
        <FontAwesomeIcon icon={sidebarOpen ? faXmark : faBars} />
      </button>

      <Sidebar
        isOpen={sidebarOpen}
        toggleSidebar={toggleSidebar}
        isCollapsed={sidebarCollapsed}
        toggleCollapsed={toggleSidebarCollapsed}
      />
      <main className="main-content">{routes}</main>
    </div>
  );
}


export default App;
